import { ApolloError } from "@apollo/client";

type MaybeApolloError = Partial<ApolloError> & {
  graphQLErrors?: Array<{
    message?: string;
    path?: string[];
    extensions?: {
      category?: string;
      validation?: Record<string, string[]>;
    };
  }>;
  message?: string;
};

const flattenValidationErrors = (validation?: Record<string, string[]>) => {
  if (!validation) {
    return null;
  }

  const messages = Object.entries(validation).flatMap(([field, errors]) =>
    errors.map((error) => error.replace("input.", "").replace(field, field.replace("input.", "")))
  );

  return messages.length ? messages.join(" ") : null;
};

export default function getGraphQLErrorMessage(error: unknown, fallback: string) {
  const apolloError = error as MaybeApolloError | undefined;
  const graphQLError = apolloError?.graphQLErrors?.[0];
  const message = graphQLError?.message ?? apolloError?.message ?? "";
  const validationMessage = flattenValidationErrors(graphQLError?.extensions?.validation);

  if (validationMessage) {
    if (validationMessage.includes("materialNoPlants")) {
      return "A no-plant material was deleted or changed. Refresh the proposal and try again.";
    }

    return validationMessage;
  }

  if (message === "Unauthenticated.") {
    return "Your session expired. Please sign in again.";
  }

  if (message === "content_type_not_allowed") {
    return "Only JPG, PNG, and PDF files are allowed.";
  }

  if (message.includes("value.subcontractorCost")) {
    return "Subcontractor cost is invalid. Clear it and enter a whole-dollar amount again.";
  }

  if (message.includes("value.material.unitPrice")) {
    return "Unit price is invalid. Clear it and enter the value again.";
  }

  if (message.includes("value.materialNoPlants") && message.includes(".percentage")) {
    return "No-plant material percentage is required and must be between 0 and 100.";
  }

  if (message.includes("value.materialNoPlants") && message.includes(".quantityOverride")) {
    return "No-plant material quantity must be a whole number.";
  }

  if (message.includes("value.materialNoPlants") && message.includes(".id")) {
    return "A no-plant material is unsaved. Add it again and then retry saving.";
  }

  if (message.includes("requires at least one product")) {
    return message;
  }

  if (message === "Internal server error") {
    const path = graphQLError?.path?.[0];
    if (path === "updateProposalProduct") {
      return "We could not save this proposal change. Please refresh and try again.";
    }

    if (path === "previewProposal") {
      return "We could not generate the proposal preview. Please refresh and try again.";
    }
  }

  return message || fallback;
}
